import type { ReactNode } from 'react';
import { StackProvider, StackTheme } from '@stackframe/react';
import { stackClientApp, isNeonAuthEnabled } from '@/utils/auth.utils';

interface NeonAuthProviderProps {
  children: ReactNode;
}

/**
 * Neon Auth Provider
 *
 * Wraps the application with Neon Auth (Stack) authentication providers.
 * This component provides:
 * - StackProvider: Authentication context for the entire app
 * - StackTheme: Consistent styling for auth components
 *
 * If Neon Auth is disabled (VITE_NEON_AUTH_ENABLED !== 'true'),
 * this provider simply renders children without any Stack components.
 *
 * Note: StackHandler is not used here as it requires specific routing setup.
 * Authentication routing is handled by the application's own routing logic.
 *
 * Usage:
 * Wrap your main App component with this provider:
 * ```tsx
 * <NeonAuthProvider>
 *   <App />
 * </NeonAuthProvider>
 * ```
 */
export function NeonAuthProvider({ children }: NeonAuthProviderProps) {
  // If Neon Auth is not enabled or not configured, just render children
  if (!isNeonAuthEnabled || !stackClientApp) {
    return <>{children}</>;
  }

  return (
    <StackProvider app={stackClientApp}>
      <StackTheme>{children}</StackTheme>
    </StackProvider>
  );
}
